/**
 * Ask layer core: validation of catalog entries and of the two request bodies.
 * ZERO dependencies — a hand-written twin of the rules `catalog/schema.json` describes.
 *
 *
 * Behaviour, deliberately identical to the Zod schema it replaces:
 *   - every object is STRICT: an unknown key is an error, never silently dropped. `descriptions:`
 *     must fail loudly instead of publishing an entry with an empty description;
 *   - strings are trimmed before the length check, and the trimmed value is what comes out;
 *   - optional means absent (`undefined`); `null` is rejected like any wrong type;
 *   - cross-field rules run even when a field failed, so one pass reports everything fixable;
 *   - issue paths read like the frontmatter: `i18n.de.job`, `aliases[3]`, `action.reversible`.
 
 */
import {
  ASSISTANT_COMMENT_MAX,
  ASSISTANT_LIMIT_MAX,
  ASSISTANT_LIMIT_MIN,
  ASSISTANT_QUERY_ID_MAX,
  ASSISTANT_QUESTION_MAX,
  ASSISTANT_SCREEN_MAX,
  CATALOG_ACTION_CONFIRMATION_MAX,
  CATALOG_ACTION_OPERATION_MAX,
  CATALOG_ACTION_REQUIRES_MAX,
  CATALOG_ALIASES_MAX,
  CATALOG_ALIAS_MAX,
  CATALOG_ANCHOR_ACTIONS,
  CATALOG_ANCHOR_MAX,
  CATALOG_AREAS,
  CATALOG_BASE_LANGUAGE,
  CATALOG_DESCRIPTION_MAX,
  CATALOG_JOB_MAX,
  CATALOG_LANGUAGES,
  CATALOG_OWNER_MAX,
  CATALOG_PREREQUISITES_MAX,
  CATALOG_ROLES,
  CATALOG_SOURCE_REF_MAX,
  CATALOG_STATUSES,
  type AssistantFeedback,
  type AssistantSearchRequest,
  type CatalogAction,
  type CatalogAnchorAction,
  type CatalogArea,
  type CatalogEntry,
  type CatalogEntryI18n,
  type CatalogLanguage,
  type CatalogRole,
  type CatalogStatus,
  type CatalogTranslation,
} from "./types";

export interface ValidationIssue {
  /** Dotted path into the input — `i18n.de.job`, `aliases[3]`; empty for the whole object. */
  path: string;
  message: string;
}

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; issues: ValidationIssue[] };

// ---------------------------------------------------------------------------- primitives

const ID_RE = /^[A-Z]{3}-\d{3}$/;
const ROUTE_RE = /^\/[^\s]*$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const describe = (value: unknown): string =>
  value === null ? "null" : Array.isArray(value) ? "array" : typeof value;

class Collector {
  readonly issues: ValidationIssue[] = [];
  add(path: string, message: string): void {
    this.issues.push({ path, message });
  }
  result<T>(value: T): ValidationResult<T> {
    return this.issues.length === 0 ? { ok: true, value } : { ok: false, issues: this.issues };
  }
}

const join = (base: string, key: string): string => (base ? `${base}.${key}` : key);

/**
 * `z.string().trim().min(min).max(max)` — a string, trimmed, within bounds. Returns the trimmed
 * value, or `undefined` after recording the issue. `min: 0` allows the empty string (feedback
 * comments, the current screen); everything else in the catalog is `min: 1`.
 */
const text = (
  c: Collector,
  path: string,
  value: unknown,
  max: number,
  min = 1
): string | undefined => {
  if (typeof value !== "string") {
    c.add(path, `expected a string, received ${describe(value)}`);
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed.length < min) {
    c.add(path, "must not be empty");
    return undefined;
  }
  if (trimmed.length > max) {
    c.add(path, `must be at most ${max} characters (got ${trimmed.length})`);
    return undefined;
  }
  return trimmed;
};

const oneOf = <T extends string>(
  c: Collector,
  path: string,
  value: unknown,
  allowed: readonly T[]
): T | undefined => {
  if (typeof value === "string" && (allowed as readonly string[]).includes(value))
    return value as T;
  c.add(path, `must be one of: ${allowed.join(", ")} (received ${JSON.stringify(value)})`);
  return undefined;
};

const boolean = (c: Collector, path: string, value: unknown): boolean | undefined => {
  if (typeof value === "boolean") return value;
  c.add(path, `expected a boolean, received ${describe(value)}`);
  return undefined;
};

/** `z.array(shortText(itemMax)).max(maxItems)` with per-item paths like `aliases[3]`. */
const textArray = (
  c: Collector,
  path: string,
  value: unknown,
  itemMax: number,
  maxItems: number
): string[] | undefined => {
  if (!Array.isArray(value)) {
    c.add(path, `expected an array, received ${describe(value)}`);
    return undefined;
  }
  if (value.length > maxItems) c.add(path, `at most ${maxItems} items (got ${value.length})`);
  const before = c.issues.length;
  const out: string[] = [];
  value.forEach((item, index) => {
    const checked = text(c, `${path}[${index}]`, item, itemMax);
    if (checked !== undefined) out.push(checked);
  });
  return c.issues.length === before ? out : undefined;
};

/** Strict objects: every key must be known. One issue per unknown key, named, so a typo reads. */
const rejectUnknownKeys = (
  c: Collector,
  path: string,
  value: Record<string, unknown>,
  known: readonly string[]
): void => {
  for (const key of Object.keys(value)) {
    if (!known.includes(key)) c.add(join(path, key), `unknown key "${key}"`);
  }
};

// ---------------------------------------------------------------------------- catalog entry

const TRANSLATION_KEYS = ["job", "description", "prerequisites", "aliases"] as const;

const translation = (
  c: Collector,
  path: string,
  value: unknown
): CatalogTranslation | undefined => {
  if (!isRecord(value)) {
    c.add(path, `expected an object, received ${describe(value)}`);
    return undefined;
  }
  rejectUnknownKeys(c, path, value, TRANSLATION_KEYS);
  const before = c.issues.length;
  const out: CatalogTranslation = {
    job: text(c, join(path, "job"), value.job, CATALOG_JOB_MAX) ?? "",
    description:
      text(c, join(path, "description"), value.description, CATALOG_DESCRIPTION_MAX) ?? "",
  };
  if (value.prerequisites !== undefined) {
    const checked = text(
      c,
      join(path, "prerequisites"),
      value.prerequisites,
      CATALOG_PREREQUISITES_MAX
    );
    if (checked !== undefined) out.prerequisites = checked;
  }
  if (value.aliases !== undefined) {
    const checked = textArray(
      c,
      join(path, "aliases"),
      value.aliases,
      CATALOG_ALIAS_MAX,
      CATALOG_ALIASES_MAX
    );
    if (checked !== undefined) out.aliases = checked;
  }
  return c.issues.length === before ? out : undefined;
};

const i18n = (c: Collector, value: unknown): CatalogEntryI18n | undefined => {
  if (!isRecord(value)) {
    c.add("i18n", `expected an object of translations by language, received ${describe(value)}`);
    return undefined;
  }
  const before = c.issues.length;
  const out: CatalogEntryI18n = {};
  for (const [language, block] of Object.entries(value)) {
    const path = `i18n.${language}`;
    if (!(CATALOG_LANGUAGES as readonly string[]).includes(language)) {
      c.add(path, `unknown language "${language}" — add it to CATALOG_LANGUAGES first`);
      continue;
    }
    if (language === CATALOG_BASE_LANGUAGE) {
      c.add(
        path,
        `${CATALOG_BASE_LANGUAGE} is the base language — write it in the top-level fields, not under i18n`
      );
      continue;
    }
    const checked = translation(c, path, block);
    if (checked !== undefined) out[language as CatalogLanguage] = checked;
  }
  return c.issues.length === before ? out : undefined;
};

const ACTION_KEYS = ["operation", "requires", "reversible", "confirmation"] as const;

const action = (c: Collector, value: unknown): CatalogAction | undefined => {
  if (!isRecord(value)) {
    c.add("action", `expected an object, received ${describe(value)}`);
    return undefined;
  }
  rejectUnknownKeys(c, "action", value, ACTION_KEYS);
  const before = c.issues.length;
  const out: CatalogAction = {
    operation: text(c, "action.operation", value.operation, CATALOG_ACTION_OPERATION_MAX) ?? "",
    reversible: boolean(c, "action.reversible", value.reversible) ?? false,
    confirmation:
      text(c, "action.confirmation", value.confirmation, CATALOG_ACTION_CONFIRMATION_MAX) ?? "",
  };
  if (value.requires !== undefined) {
    const checked = text(c, "action.requires", value.requires, CATALOG_ACTION_REQUIRES_MAX);
    if (checked !== undefined) out.requires = checked;
  }
  return c.issues.length === before ? out : undefined;
};

const ENTRY_KEYS = [
  "id",
  "area",
  "job",
  "description",
  "prerequisites",
  "aliases",
  "roles",
  "deeplink",
  "anchor",
  "anchor_route",
  "anchor_surface",
  "anchor_action",
  "source_ref",
  "status",
  "owner",
  "i18n",
  "action",
] as const;

/**
 * One catalog entry: the frontmatter of `catalog/<area>/<ID>.md` as a plain object.
 * `catalog-parse.ts` calls this; a file that parses but does not validate is rejected at build
 * time, never at retrieval time.
 */
export const validateCatalogEntry = (raw: unknown): ValidationResult<CatalogEntry> => {
  const c = new Collector();
  if (!isRecord(raw)) {
    c.add("", `expected an object, received ${describe(raw)}`);
    return c.result(undefined as never);
  }
  rejectUnknownKeys(c, "", raw, ENTRY_KEYS);

  // --- fields ------------------------------------------------------------------------------
  let id = "";
  if (typeof raw.id !== "string") c.add("id", `expected a string, received ${describe(raw.id)}`);
  else if (!ID_RE.test(raw.id))
    c.add("id", "id must look like BEN-001 (three letters, dash, three digits)");
  else id = raw.id;

  const area = oneOf<CatalogArea>(c, "area", raw.area, CATALOG_AREAS);
  const job = text(c, "job", raw.job, CATALOG_JOB_MAX);
  const description = text(c, "description", raw.description, CATALOG_DESCRIPTION_MAX);
  const prerequisites =
    raw.prerequisites === undefined
      ? undefined
      : text(c, "prerequisites", raw.prerequisites, CATALOG_PREREQUISITES_MAX);
  const aliases =
    raw.aliases === undefined
      ? undefined
      : textArray(c, "aliases", raw.aliases, CATALOG_ALIAS_MAX, CATALOG_ALIASES_MAX);

  let roles: CatalogRole[] | undefined;
  if (!Array.isArray(raw.roles)) {
    c.add("roles", `expected an array, received ${describe(raw.roles)}`);
  } else if (raw.roles.length === 0) {
    c.add("roles", "at least one role — who is this a normal part of the job for?");
  } else {
    const before = c.issues.length;
    const checked = raw.roles.map((role, index) =>
      oneOf<CatalogRole>(c, `roles[${index}]`, role, CATALOG_ROLES)
    );
    if (c.issues.length === before) roles = checked as CatalogRole[];
  }

  let deeplink: string | undefined;
  if (typeof raw.deeplink !== "string") {
    c.add("deeplink", `expected a string, received ${describe(raw.deeplink)}`);
  } else {
    const trimmed = raw.deeplink.trim();
    if (!ROUTE_RE.test(trimmed)) c.add("deeplink", "deeplink must be an app route starting with /");
    // Literal only: the panel NAVIGATES here, and nobody can navigate to `/orders/:id`. The route
    // that renders the control goes in `anchor_route`, which may carry params because it is
    // matched, never navigated to. (`catalog-check` enforced this downstream against the router;
    // saying it here names the fix in the same breath as the error.)
    else if (trimmed.includes(":") || trimmed.includes("*")) {
      c.add(
        "deeplink",
        "deeplink must be a literal route — no :param and no *; put the record page in anchor_route and link to the list"
      );
    } else deeplink = trimmed;
  }

  const anchor =
    raw.anchor === undefined ? undefined : text(c, "anchor", raw.anchor, CATALOG_ANCHOR_MAX);

  let anchorRoute: string | undefined;
  if (raw.anchor_route !== undefined) {
    if (typeof raw.anchor_route !== "string") {
      c.add("anchor_route", `expected a string, received ${describe(raw.anchor_route)}`);
    } else {
      const trimmed = raw.anchor_route.trim();
      if (!ROUTE_RE.test(trimmed))
        c.add("anchor_route", "anchor_route must be an app route starting with /");
      else anchorRoute = trimmed;
    }
  }

  const anchorSurface =
    raw.anchor_surface === undefined
      ? undefined
      : text(c, "anchor_surface", raw.anchor_surface, CATALOG_ANCHOR_MAX);
  const anchorAction =
    raw.anchor_action === undefined
      ? undefined
      : oneOf<CatalogAnchorAction>(c, "anchor_action", raw.anchor_action, CATALOG_ANCHOR_ACTIONS);
  const sourceRef =
    raw.source_ref === undefined
      ? undefined
      : text(c, "source_ref", raw.source_ref, CATALOG_SOURCE_REF_MAX);
  const status = oneOf<CatalogStatus>(c, "status", raw.status, CATALOG_STATUSES);
  const owner = text(c, "owner", raw.owner, CATALOG_OWNER_MAX);
  const translations = raw.i18n === undefined ? undefined : i18n(c, raw.i18n);
  const actionBlock = raw.action === undefined ? undefined : action(c, raw.action);

  // --- cross-field rules — run on what was given, whether or not the fields above passed -----
  const rawAnchor = typeof raw.anchor === "string" ? raw.anchor.trim() : undefined;
  const rawRoute = typeof raw.anchor_route === "string" ? raw.anchor_route.trim() : undefined;
  const rawSurface = typeof raw.anchor_surface === "string" ? raw.anchor_surface.trim() : undefined;
  const rawDeeplink = typeof raw.deeplink === "string" ? raw.deeplink.trim() : undefined;

  if (raw.anchor_action !== undefined && !rawAnchor) {
    c.add("anchor_action", "anchor_action needs an anchor to act on");
  }
  if (raw.anchor_route !== undefined && !rawAnchor) {
    c.add("anchor_route", "anchor_route needs an anchor to locate");
  }
  if (raw.anchor_surface !== undefined && !rawAnchor) {
    c.add("anchor_surface", "anchor_surface needs an anchor to reveal");
  }
  // A control that opens the surface it is already inside cannot be pressed to get there.
  if (rawSurface && rawSurface === rawAnchor) {
    c.add(
      "anchor_surface",
      "anchor_surface must name the control that OPENS the surface, not the anchor inside it"
    );
  }
  // A control the panel is allowed to PRESS must be on the page the panel can navigate to.
  // Anywhere else the press would either never happen or land on whichever record the user
  // happens to have open — which is the one thing `open` promises never to do.
  if (raw.anchor_action === "open" && rawRoute && rawRoute !== rawDeeplink) {
    c.add(
      "anchor_action",
      'anchor_action "open" needs the anchor on the deep-linked page — an off-page control can only be described, never pressed'
    );
  }

  if (c.issues.length > 0) return c.result(undefined as never);

  const entry: CatalogEntry = {
    id,
    area: area as CatalogArea,
    job: job as string,
    description: description as string,
    roles: roles as CatalogRole[],
    deeplink: deeplink as string,
    status: status as CatalogStatus,
    owner: owner as string,
  };
  if (prerequisites !== undefined) entry.prerequisites = prerequisites;
  if (aliases !== undefined) entry.aliases = aliases;
  if (anchor !== undefined) entry.anchor = anchor;
  if (anchorRoute !== undefined) entry.anchor_route = anchorRoute;
  if (anchorSurface !== undefined) entry.anchor_surface = anchorSurface;
  if (anchorAction !== undefined) entry.anchor_action = anchorAction;
  if (sourceRef !== undefined) entry.source_ref = sourceRef;
  if (translations !== undefined) entry.i18n = translations;
  if (actionBlock !== undefined) entry.action = actionBlock;
  return c.result(entry);
};

// ---------------------------------------------------------------------------- request bodies

const SEARCH_KEYS = ["question", "screen", "language", "limit"] as const;

/** Body of `POST /search`. Strict: a client sending a field the server ignores is a client bug. */
export const validateSearchRequest = (raw: unknown): ValidationResult<AssistantSearchRequest> => {
  const c = new Collector();
  if (!isRecord(raw)) {
    c.add("", `expected an object, received ${describe(raw)}`);
    return c.result(undefined as never);
  }
  rejectUnknownKeys(c, "", raw, SEARCH_KEYS);

  const question = text(c, "question", raw.question, ASSISTANT_QUESTION_MAX);
  const screen =
    raw.screen === undefined ? undefined : text(c, "screen", raw.screen, ASSISTANT_SCREEN_MAX, 0);
  const language =
    raw.language === undefined
      ? undefined
      : oneOf<CatalogLanguage>(c, "language", raw.language, CATALOG_LANGUAGES);

  let limit: number | undefined;
  if (raw.limit !== undefined) {
    if (typeof raw.limit !== "number" || Number.isNaN(raw.limit)) {
      c.add("limit", `expected a number, received ${describe(raw.limit)}`);
    } else if (!Number.isInteger(raw.limit)) {
      c.add("limit", "expected an integer");
    } else if (raw.limit < ASSISTANT_LIMIT_MIN || raw.limit > ASSISTANT_LIMIT_MAX) {
      c.add("limit", `must be between ${ASSISTANT_LIMIT_MIN} and ${ASSISTANT_LIMIT_MAX}`);
    } else limit = raw.limit;
  }

  if (c.issues.length > 0) return c.result(undefined as never);
  const request: AssistantSearchRequest = { question: question as string };
  if (screen !== undefined) request.screen = screen;
  if (language !== undefined) request.language = language;
  if (limit !== undefined) request.limit = limit;
  return c.result(request);
};

const FEEDBACK_KEYS = ["queryId", "helpful", "comment", "deeplinkFollowed"] as const;

/**
 * Body of `POST /feedback`. At least one of the two SIGNALS must be present — `helpful` or
 * `deeplinkFollowed`. A comment alone is not a signal: it says nothing about whether the answer
 * was right, which is what the two rates measure.
 */
export const validateFeedback = (raw: unknown): ValidationResult<AssistantFeedback> => {
  const c = new Collector();
  if (!isRecord(raw)) {
    c.add("", `expected an object, received ${describe(raw)}`);
    return c.result(undefined as never);
  }
  rejectUnknownKeys(c, "", raw, FEEDBACK_KEYS);

  const queryId = text(c, "queryId", raw.queryId, ASSISTANT_QUERY_ID_MAX);
  const helpful = raw.helpful === undefined ? undefined : boolean(c, "helpful", raw.helpful);
  const comment =
    raw.comment === undefined
      ? undefined
      : text(c, "comment", raw.comment, ASSISTANT_COMMENT_MAX, 0);
  const deeplinkFollowed =
    raw.deeplinkFollowed === undefined
      ? undefined
      : boolean(c, "deeplinkFollowed", raw.deeplinkFollowed);

  if (raw.helpful === undefined && raw.deeplinkFollowed === undefined) {
    c.add("", "feedback must carry a thumbs verdict, a deep-link follow, or both");
  }

  if (c.issues.length > 0) return c.result(undefined as never);
  const feedback: AssistantFeedback = { queryId: queryId as string };
  if (helpful !== undefined) feedback.helpful = helpful;
  if (comment !== undefined) feedback.comment = comment;
  if (deeplinkFollowed !== undefined) feedback.deeplinkFollowed = deeplinkFollowed;
  return c.result(feedback);
};

/** `path: message` lines, for error output. Empty path reads as `(root)`. */
export const formatIssues = (issues: readonly ValidationIssue[]): string =>
  issues.map((issue) => `${issue.path || "(root)"}: ${issue.message}`).join("; ");
